<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" style="display: inline;">
        <slot></slot>
      </div>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ $t('routes.pod.menuAccesslink.editShareLink') }}</span>
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" color="primary" show-arrows>
        <v-tab>{{ $t('routes.pod.menuAccesslink.shareLinkInformation') }}</v-tab>
        <v-tab>{{ $t('routes.pod.menuAccesslink.emailInvitations') }}</v-tab>
        <v-tab>{{ $t('routes.pod.menuAccesslink.options') }}</v-tab>
      </v-tabs>

      <v-card-text class="scrollable">
        <v-form ref="form" v-on:submit.prevent="submitForm" :disabled="false" autocomplete="off">
          <v-tabs-window v-model="tab" :style="{ backgroundColor: 'unset !important' }">
            <v-tabs-window-item class="pt-4">
              <v-row v-if="accesslink.name">
                <v-col cols="12">
                  <v-text-field
                    :model-value="accesslink.name"
                    :label="$t('routes.node.name')"
                    disabled
                    variant="solo-filled"
                    hide-details
                    readonly
                    flat
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    type="text"
                    :label="$t('routes.pod.menuAccesslink.password')"
                    v-model="password"
                    :rules="passwordRules"
                    variant="solo-filled"
                    hide-details
                    required
                    flat
                    data-cy="podMenu-tools-manageGenericLinks-password"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip
                        v-if="canAccessClipboard && password && password.length > 0"
                        location="bottom"
                        :aria-label="$t('routes.pod.menuAccesslink.copyPassword')"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            @click="copyPasswordToClipboard"
                            :aria-label="$t('routes.pod.menuAccesslink.copyPassword')"
                            variant="plain"
                            icon
                          >
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('routes.pod.menuAccesslink.copyPassword') }}</span>
                      </v-tooltip>
                      <v-tooltip location="bottom" :aria-label="$t('routes.pod.menuAccesslink.generatePassword')">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            @click="generatePassword"
                            :aria-label="$t('routes.pod.menuAccesslink.generatePassword')"
                            variant="plain"
                            icon
                          >
                            <v-icon>mdi-lock-reset</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('routes.pod.menuAccesslink.generatePassword') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-menu
                    v-model="datepickerMenu"
                    :close-on-content-click="false"
                    :offset="40"
                    transition="scale-transition"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        v-model="expiryDate"
                        :placeholder="$t('routes.pod.menuAccesslink.expiryDate')"
                        :aria-label="$t('routes.pod.menuAccesslink.expiryDate')"
                        :rules="expiryDateRules"
                        append-inner-icon="mdi-calendar"
                        variant="solo-filled"
                        hide-details="auto"
                        readonly
                        required
                        flat
                        v-bind="props"
                        data-cy="podMenu-tools-manageGenericLinks-dateMenu"
                      />
                    </template>
                    <v-date-picker
                      v-model="expiryDate"
                      @update:model-value="datepickerMenu = false"
                      :min="getMinDatepicker"
                      data-cy="podMenu-tools-manageGenericLinks-datePicker"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <div data-cy="podMenu-tools-manageGenericLinks-notes" style="height: 220px">
                    <MdEditor
                      v-model="linkNotes"
                      :theme="$vuetify.theme.name === 'dark' ? 'dark' : 'light'"
                      :preview="false"
                      :placeholder="$t('routes.pod.menuAccesslink.notes')"
                      noUploadImg
                      :toolbars="[
                        'bold', 'italic', 'strikeThrough', 'title', '-', 'code', 'unorderedList',
                        'orderedList', '-', 'link', 'image', 'table', '-', 'previewOnly']"
                      :footers="[]"
                      language="en-US"
                      style="height: 220px;"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-tabs-window-item>

            <v-tabs-window-item class="pt-4">
              <div>
                <v-form ref="emailsForm" class="mb-2" @submit.prevent="addEmailRecipient">
                  <v-text-field
                    v-model="addEmailInput"
                    :rules="addEmailInputRules"
                    :label="$t('routes.pod.menuAccesslink.emailAddresses')"
                    :placeholder="$t('routes.pod.menuAccesslink.enterEmailAddressAndPress')"
                    variant="solo-filled"
                    flat
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        height="28"
                        density="comfortable"
                        variant="elevated"
                        v-show="addEmailInput"
                        @click="addEmailRecipient"
                      >
                        <v-icon>mdi-plus-circle</v-icon>
                        {{ $t('common.add') }}
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-form>
                <div v-if="emailRecipientsList.length > 0" class="mb-4">
                  <v-chip
                    v-for="email in emailRecipientsList"
                    :key="email"
                    @click:close="removeEmailRecipient(email)"
                    color="primary"
                    variant="flat"
                    :text="email"
                    :close-label="$t('routes.pod.menuAccesslink.removeEmailRecipient', { email })"
                    close-icon="mdi-close"
                    closable
                  />
                </div>
              </div>
              <div>
                <v-textarea
                  v-model="emailNote"
                  :label="$t('menus.menuUploadLink.noteToEmail')"
                  variant="solo-filled"
                  :rows="3"
                  class="mt-3"
                  flat
                />
              </div>
              <div>
                <v-checkbox
                  v-model="includeQrCode"
                  :label="$t('routes.pod.menuAccesslink.addQrCodeToEmail')"
                  hide-details
                  density="compact"
                />
              </div>
              <div
                v-if="accesslink?.invitations && Array.isArray(accesslink.invitations) && accesslink.invitations.length > 0"
                class="mt-4"
              >
                <span class="font-weight-bold">{{ $t('routes.pod.menuAccesslink.invitedPeople') }}:</span>
                {{ accesslink.invitations.map((i) => i.email).join(', ') }}
              </div>
            </v-tabs-window-item>

            <v-tabs-window-item class="pt-4">
              <v-row>
                <v-col>
                  <div data-cy="podMenu-tools-manageGenericLinks-teamOnly" v-if="isTeamMemberUser">
                    <v-checkbox
                      v-if="$isTeamUser()"
                      v-model="teamOnly"
                      :disabled="isTeamonlyDisabled"
                      :label="$t('routes.pod.menuAccesslink.teamOnly')"
                      hide-details
                      required
                      density="compact"
                    />
                    <div v-if="isTeamonlyDisabled">
                      {{ $t('routes.pod.menuAccesslink.teamonlyMustBeChecked') }}
                    </div>
                  </div>
                  <div data-cy="podMenu-tools-manageGenericLinks-showAssets">
                    <v-checkbox
                      v-model="showAssets"
                      :label="$t('routes.pod.menuAccesslink.showAssets')"
                      hide-details
                      required
                      density="compact"
                    />
                  </div>
                  <div data-cy="podMenu-tools-manageGenericLinks-showSubtitles">
                    <v-checkbox
                      v-model="showSubtitles"
                      :disabled="!!showAssets"
                      :label="$t('routes.pod.menuAccesslink.showSubtitles')"
                      hide-details
                      required
                      density="compact"
                    />
                  </div>
                  <div data-cy="podMenu-tools-manageGenericLinks-allowDownloading">
                    <v-checkbox
                      v-model="allowDownloading"
                      :label="$t('routes.pod.menuAccesslink.allowDownloading')"
                      hide-details
                      required
                      density="compact"
                    />
                  </div>
                  <div data-cy="podMenu-tools-manageGenericLinks-allowComments">
                    <v-checkbox
                      v-model="allowComments"
                      :disabled="isCommentsDisabled"
                      :label="$t('routes.pod.menuAccesslink.allowComments')"
                      hide-details
                      required
                      density="compact"
                    />
                    <div v-if="showCommentsErrorMessage" class="pl-1">
                      {{ $t('routes.pod.menuAccesslink.commentsAreNotAllowed') }}
                    </div>
                  </div>
                  <div data-cy="podMenu-tools-manageGenericLinks----">
                    <v-checkbox
                      v-model="allowPlayingRaw"
                      :label="$t('routes.pod.menuAccesslink.allowPlayingRaw')"
                      hide-details
                      required
                      density="compact"
                    />
                  </div>
                  <div class="pb-4" data-cy="podMenu-tools-manageGenericLinks-addVersions">
                    <v-checkbox
                      v-model="addVersions"
                      :label="$t('routes.pod.menuAccesslink.addVersions')"
                      hide-details
                      required
                      density="compact"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-tabs-window-item>
          </v-tabs-window>

          <div v-if="orderedNodes.length > 0" class="mt-4">
            <h4>{{ $t('routes.pod.menuAccesslink.selectedFolders') }}</h4>
            <p v-if="someSelectedFolderHasChildren" class="font-italic my-3">
              {{ $t('routes.pod.menuAccesslink.onlyOneLevelOfChildrenFolderAccessibleLine1') }}
              <br/>
              {{ $t('routes.pod.menuAccesslink.onlyOneLevelOfChildrenFolderAccessibleLine2') }}
            </p>

            <v-list density="compact">
              <v-list-item v-for="node in orderedNodes" :key="node.id">
                <div>
                  <span v-if="node.parentid && getNodeById(node.parentid)">
                    {{ getNodeById(node.parentid).name }} /
                  </span>
                  {{ node.name }}
                </div>

                <template v-slot:append>
                  <v-list-item-action class="my-0">
                    <v-small-icon-btn rounded="circle" @click="removeNode(node)">
                      <v-icon>mdi-close</v-icon>
                    </v-small-icon-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="orderedPods.length > 0" class="mt-4">
            <h4>{{ $t('routes.pod.menuAccesslink.selectedFiles') }}: </h4>
            <v-list density="compact">
              <v-list-item v-for="pod in orderedPods" :key="pod.id">
                <div>
                  <span v-if="pod.nodeid && getNodeById(pod.nodeid)">
                    {{ getNodeById(pod.nodeid).name }} /
                  </span>
                  {{ pod.name }}
                </div>

                <template v-slot:append>
                  <v-list-item-action class="my-0">
                    <v-small-icon-btn rounded="circle" @click="removePod(pod)">
                      <v-icon>mdi-close</v-icon>
                    </v-small-icon-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="pods.length === 0 && nodes.length === 0" class="mt-4 text-center bg-error">
            {{ $t('routes.node.noFileOrFolderSelected') }}
          </div>

          <div class="mt-4 text-center">
            <SelectPodsDialog
              :browseNodeId="rootNode.id"
              @selectedPods="addPodByIds"
              @selectedNodes="addNodeByIds"
            >
              <v-btn color="primary" elevation="0">
                {{ $t('routes.node.addFilesOrFolders') }}
              </v-btn>
            </SelectPodsDialog>
          </div>

          <div data-cy="podMenu-tools-manageGenericLinks-messages">
            <div v-show="successMessage" class="bg-success mt-2" data-cy="------">
              {{ successMessage }}
            </div>
            <div v-show="errorMessage" class="bg-error mt-2" data-cy="-----">{{ errorMessage }}
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="submitForm"
          :disabled="false"
          color="primary"
          variant="flat"
          elevation=0
          data-cy="dialog--ok"
        >
          {{ $t('common.update') }}
        </v-btn>
        <v-btn @click="dialog = false" variant="text">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import { set } from 'date-fns/set';
import { utc, UTCDate } from '@date-fns/utc';
import { MdEditor } from 'md-editor-v3';

import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import SelectPodsDialog from '../../../pods2/components/dialogs/SelectPodsDialog.vue';
import MimetypeHelper from '../../../../components/MimetypeHelper.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import Helper from '@/components/helper';
import { formatDate, formatDateTimeMilliseconds } from '@/components/formatters.js';

export default {
  mixins: [DialogActionKeys],
  name: "AccesslinkUpdateDialog",
  components: {
    SelectPodsDialog,
    MdEditor,
  },
  props: {
    rootNode: {
      type: Object,
      required: true,
    },
    accesslink: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      tab: 0,

      pods: [],
      nodes: [],

      password: "",
      passwordRules: [
        (v) => {
          const value = v ? v.trim() : v;
          let message = null;

          if (value) {
            if (value.length < 8) {
              return this.$t('routes.pod.menuAccesslink.passwordMinLength');
            } else if (value.length > 191) {
              return this.$t('routes.pod.menuAccesslink.passwordMaxLength');
            } else if (!Helper.isValidPassword(value)) {
              return this.$t('menus.createUser.passwordInvalidChars');
            }
          }
          return message ?? true;
        },
      ],

      datepickerMenu: false,
      expiryDateValue: new Date(),
      expiryDateRules: [
        v => !!v || this.$t('routes.pod.menuAccesslink.expiryDateRequired')
      ],

      showAssets: false,
      showSubtitles: false,
      allowDownloading: true,
      allowComments: false,
      allowPlayingRaw: false,
      addVersions: false,
      teamOnly: false,
      linkNotes: "",

      errorMessage: "",
      successMessage: "",

      simplemdeConfigs: {
        spellChecker: false,
        placeholder: this.$t('routes.pod.menuAccesslink.notes'),
        toolbar: [
          'bold', 'italic', 'strikethrough', 'heading', '|',
          'code', 'unordered-list', 'ordered-list', '|',
          'link', 'image', 'table', '|',
          'preview', 'guide'
        ],
        status: false,
      },

      addEmailInput: "",
      addEmailInputRules: Helper.getEmailValidationRules(this, { required: false }),

      emailRecipientsList: [],
      emailNote: '',

      includeQrCode: false,
    };

  },
  mounted: function() {
    this.resetForm();

    // refresh pod
    this.accesslink.pods.map((pod) => this.getPodById(pod.id));
  },
  watch: {
    dialog(newDialog) {
      if (newDialog) {
        this.resetForm();
      }
    },
    isTeamonlyDisabled(newValue) {
      if (newValue) {
        this.teamOnly = true;
      }
    },
    isCommentsDisabled(newValue) {
      if (newValue) {
        this.allowComments = false;
      }
    },
    showAssets(newValue) {
      if (newValue) {
        this.showSubtitles = false;
      }
    }
  },
  methods: {
    resetForm() {
      // this.resetSelectedPod();
      this.tab = 0;
      this.pods = [];
      this.nodes = [];

      for (let i = 0; i < this.accesslink.pods.length; i++) {
        const pod = this.getPodById(this.accesslink.pods[i].id);
        if (pod) {
          this.pods.push(pod);
        }
      }

      for (let i = 0; i < this.accesslink.nodes.length; i++) {
        const node = this.getNodeById(this.accesslink.nodes[i].id);
        if (node) {
          this.nodes.push(node);
        }
      }

      this.password = this.accesslink.password || "";
      this.linkNotes = this.accesslink.note || "";
      this.showAssets = this.accesslink.params.showassets === 1;
      this.showSubtitles = this.accesslink.params.showsubtitles === 1;
      this.allowDownloading = this.accesslink.params.allowdownloading === 1;
      this.allowComments = this.accesslink.params.allowcomments === 1;
      this.allowPlayingRaw = this.accesslink.params.allowplayingraw === 1;
      this.addVersions = this.accesslink.params.addversions === 1;
      this.teamOnly = this.accesslink.teamonly;
      this.expiryDateValue = formatDate(utc(this.accesslink.expireAt));

      if (this.isCommentsDisabled) {
        this.allowComments = 0;
      }

      this.addEmailInput = "";

      this.emailRecipientsList = [];
      this.emailNote = '';

      this.includeQrCode = false;

      this.successMessage = "";
      this.errorMessage = "";
    },
    async submitForm() {
      this.errorMessage = "";
      this.successMessage = "";

      const validForm = await this.$refs.form.validate();
      if (!this.accesslink || (this.pods.length === 0 && this.nodes.length === 0) || !validForm.valid) {
        return;
      }

      let formData = {
        podids: this.pods.map(x => x.id),
        nodeids: this.nodes.map(x => x.id),
        password: this.password,
        note: this.linkNotes,
        showassets: this.showAssets ? 1 : 0,
        showsubtitles: this.showSubtitles ? 1 : 0,
        allowdownloading: this.allowDownloading ? 1 : 0,
        allowcomments: this.allowComments ? 1 : 0,
        allowplayingraw: this.allowPlayingRaw ? 1 : 0,
        addversions: this.addVersions ? 1 : 0,
        teamonly: this.teamOnly,
        // Send the date in UTC
        expireat: formatDateTimeMilliseconds(set(new UTCDate(this.expiryDate), {
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 0,
        })),
      };

      if (this.isCommentsDisabled) {
        formData.allowComments = 0;
      }

      const accesslinkStore = useAccesslinkStore();
      let VERB = accesslinkStore.createAccesslink;
      let self = this;

      if (this.accesslink) {
        formData.id = this.accesslink.id;
        VERB = accesslinkStore.updateAccesslink;
      }

      VERB(formData)
        .then(function (accesslink) {
          if (self.emailRecipientsList.length > 0) {
            return self.sendInvitations(accesslink).then((links) => {
              if (links.length > 0) {
                return links.find(x => x.id === accesslink.id);
              }

              return accesslink;
            });
          } else {
            return accesslink;
          }
        })
        .then(function() {
          accesslinkStore.refreshAccesslinks(self.rootNode.id);
          self.successMessage = self.$t('routes.pod.menuAccesslink.linkUpdated');

          setTimeout(() => {
            self.dialog = false;
          }, 2000);
        })
        .catch(function(error){
          if (error?.response?.data?.error) {
            self.errorMessage = self.$localizeMessage(error.response.data.error);
          } else {
            self.errorMessage = self.$t('backend.system-error');
            console.log(error?.response?.data); // unhandled error
          }
        });
    },
    generatePassword: function() {
      this.password = Helper.generatePassword(10);
    },
    addPodByIds(podIds) {
      for (const aPodId of podIds) {
        const isAdded = this.pods.find(x => x.id === aPodId);
        if (!isAdded) {
          this.pods.push(this.getPodById(aPodId));
        }
      }
    },
    addNodeByIds(nodeIds) {
      for (const aNodeId of nodeIds) {
        const isAdded = this.nodes.find(x => x.id === aNodeId);
        if (!isAdded) {
          this.nodes.push(this.getNodeById(aNodeId));
        }
      }
    },
    removePod: function (pod) {
      const idx = this.pods.indexOf(pod);
      console.assert(idx >= 0, `Couldn't find pod with id ${pod.id} within this.pods`);
      this.pods.splice(idx, 1);
    },
    removeNode: function (node) {
      const idx = this.nodes.indexOf(node);
      console.assert(idx >= 0, `Couldn't find node with id ${node.id} within this.nodes`);
      this.nodes.splice(idx, 1);
    },
    copyPasswordToClipboard: function() {
      if (this.password.length > 0) {
        navigator.clipboard.writeText(this.password)
      }
    },
    async addEmailRecipient() {
      const validForm = await this.$refs.emailsForm.validate();

      if (!validForm.valid || !this.addEmailInput) {
        return;
      }

      if (this.emailRecipientsList.indexOf(this.addEmailInput) >= 0) {
        this.addEmailInput = "";
        return;
      }

      this.emailRecipientsList.push(this.addEmailInput);
      this.addEmailInput = "";
    },
    removeEmailRecipient(email) {
      this.emailRecipientsList = this.emailRecipientsList.filter(x => x !== email);
    },
    async sendInvitations(accesslink) {
      const payload = {
        linkId: accesslink.id,
        receiver: this.emailRecipientsList.join(','),
        note: this.emailNote,
        includeqrcode: this.includeQrCode ? 1 : 0,
      };

      const self = this;

      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.inviteAccesslink(payload)
        .then(function (response) {
          self.emailRecipientsList = [];
          self.emailNote = '';

          return Array.isArray(response?.links) ? response.links : [];
        });
    },
  },
  computed: {
    expiryDate: {
      get() {
        return formatDate(this.expiryDateValue);
      },
      set(value) {
        this.expiryDateValue = value;
      },
    },
    getMinDatepicker: function() {
      return formatDate(new Date());
    },
    isTeamonlyDisabled: function() {
      // check if any of the pods has teamonly flag.
      if (Array.isArray(this.pods) && this.pods.length > 0) {
        if (this.pods.some((x) => x.teamonly)) {
          return true;
        }
      }

      // check if any of the nodes has teamonly flag.
      if (Array.isArray(this.nodes) && this.nodes.length > 0) {
        if (this.nodes.some((x) => x.teamonly)) {
          return true;
        }

        // check if any of the pods in the nodes has teamonly flag.
        for (const node of this.nodes) {
          if (node.pods && node.pods.length > 0) {
            if (node.pods.some((x) => x.teamonly)) {
              return true;
            }
          }
        }
      }

      return false;
    },
    isCommentsDisabled: function() {
      // comments are allowed only for video and audio pods
      return !this.hasVideoAudioOrImageFiles && (this.nodes.length === 0);
    },
    isTeamMemberUser: function() {
      return this.$canControlTenantConfig() || this.$canBelongstoTeam();
    },
    getPodById: function() {
      const podStore = usePodStore();

      return (podId) => {
        const pod = podStore.getPodById(podId);
        if (!pod) {
          podStore.fetchPodById(podId);
          return null;
        }

        return pod;
      }
    },
    getNodeById: function() {
      const nodeStore = useNodeStore();

      return (nodeId) => {
        // const nodeId = item.pod.nodeid;
        const node = nodeStore.getNodeById(nodeId);
        if (!node) {
          nodeStore.getFullNodeInfo(nodeId);
          return null;
        }

        return node;
      }
    },
    canAccessClipboard: function() {
      return window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
    },
    hasVideoAudioOrImageFiles: function() {
      const hasVideoOrAudioPod = this.pods.some((x) =>  MimetypeHelper.isMimetypeVideo(x) ||
                                                        MimetypeHelper.isMimetypeAudio(x) ||
                                                        MimetypeHelper.isMimetypeImage(x));
      return hasVideoOrAudioPod;

      // Use this logic instead of the above one if it is necessary.
      /*
      let hasSuitableFiles = false;

      if (this.nodes.length > 0) {
        for (const node of this.nodes) {
          if (node.pods && node.pods.length > 0) {
            for (const pod of node.pods) {
              if (pod.asset && pod.asset.length > 0) {
                hasSuitableFiles = pod.asset.some((x) => MimetypeHelper.isVideoAsset(x) || MimetypeHelper.isAudioAsset(x));
                if (hasSuitableFiles) {
                  break;
                }
              }
            }
          }
        }
      }

      // if there are no suitable files in nodes, check the pods.
      if (hasSuitableFiles) {
        return hasSuitableFiles;
      }

      if (this.pods && this.pods.length > 0) {
        hasSuitableFiles = this.pods.some(x => MimetypeHelper.isMimetypeVideo(x) || MimetypeHelper.isMimetypeAudio(x));
      }

      return hasSuitableFiles;
      */
    },
    showCommentsErrorMessage: function() {
      const hasPods = this.pods.length > 0;
      const hasNodes = this.nodes.length > 0;

      // comments are allowed only for video and audio pods
      // if there are folder, we will not show the error message.
      return hasPods && (!hasNodes && !this.hasVideoAudioOrImageFiles);
    },
    someSelectedFolderHasChildren: function() {
      return this.nodes.some((node) => node.subfoldersCount > 0);
    },
    orderedPods: function() {
      return [...this.pods].sort((a, b) => {
        if (a.nodeid === b.nodeid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.nodeid).name.localeCompare(this.getNodeById(b.nodeid).name);
      });
    },
    orderedNodes: function() {
      return [...this.nodes].sort((a, b) => {
        if (a.parentid === b.parentid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.parentid).name.localeCompare(this.getNodeById(b.parentid).name);
      });
    }
  }
}
</script>

<style>
</style>
